import { PaginatedObject } from '@api/shared';
import { PageChangeParams } from '@material-ui/data-grid';
import { useState, useCallback, useEffect } from 'react';
import { Observable } from 'rxjs';

//#region Hooks
function pageEvent<T extends keyof PageChangeParams>(key: T, valueSetter: Function) {
  return (params: PageChangeParams) => {
    valueSetter(params[key]);
  };
}

export function useServerPagination<K extends { id: any }, T extends PaginatedObject<K>>(
  factory: (props: { page: number; pageSize: number }) => Observable<T>,
  { defPage = 1, defPageSize = 10 } = {},
  dependencies: any[] = []
) {
  // API State
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<K[]>([]);
  const [page, setPage] = useState(defPage);
  const [pageSize, setPageSize] = useState(defPageSize);
  const [loading, setLoading] = useState(false);

  // Callables
  const onPageChange = useCallback(pageEvent('page', setPage), [setPage]);
  const onPageSizeChange = useCallback(pageEvent('pageSize', setPageSize), [setPageSize]);
  const resetPagination = useCallback(() => {
    setPage(() => defPage);
    setPageSize(() => defPageSize);
  }, [setPage, setPageSize]);

  useEffect(() => {
    setLoading(true);
    const subscription = factory({ page, pageSize }).subscribe({
      next: ({ data, count }) => {
        setRows(() => data);
        setRowCount(() => count);
      },
      complete() {
        setLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [page, pageSize, setRows, setRowCount, setLoading, ...dependencies]);

  return {
    rows,
    page,
    loading,
    pageSize,
    rowCount,
    onPageChange,
    resetPagination,
    onPageSizeChange,
  };
}
//#endregion Hooks
